import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Contact, ContactBook } from '../api/types';
import MailShell, { useMail } from '../components/MailShell';
import { Detail, Empty, Pane, button, card, input } from '../components/Section';
import { useT } from '../i18n';
import { avatarColor, initials } from '../mail/format';
import { useToasts } from '../ui/toasts';
import { usePanes } from '../ui/viewport';

/*
    The address book.

    The contacts themselves live on a CardDAV server, not here and not in the mail server — IMAP
    knows about mailboxes, not people. The same book is what a phone syncs with, which is the whole
    point of it being a server rather than a table in this application: a number saved here is on
    the phone before anybody thinks to look for it.

    That also decides how this screen edits. A card written by a phone carries a photograph, a
    birthday and a postal address, none of which are drawn here — so an edit changes the five fields
    this screen shows and the server leaves the rest of the card alone. A screen that rebuilt the
    card from what it displays would delete everything it does not display.
*/

/** What the form is editing. Its own shape, so an unsaved edit is never mistaken for a contact. */
interface Draft {
    /** Empty for a contact that does not exist yet. */
    id: string;
    name: string;
    email: string[];
    phone: string[];
    org: string;
    note: string;
    book: string;
    shared: boolean;
}

const NOTHING: unique symbol = Symbol('nothing');
/** Where a click wants to go: somebody, a new contact, or back to no selection at all. */
type Destination = Contact | 'new' | typeof NOTHING;

function draftOf(person: Contact): Draft {
    return {
        id: person.id,
        name: person.name,
        // A copy, always: the form pushes into these lists, and pushing into the loaded contact's
        // own array would edit the list behind the screen.
        email: [...person.email],
        phone: [...person.phone],
        org: person.org ?? '',
        note: person.note ?? '',
        book: person.book,
        shared: Boolean(person.shared),
    };
}

function blank(book: ContactBook | undefined): Draft {
    return {
        id: '',
        name: '',
        email: [''],
        phone: [''],
        org: '',
        note: '',
        book: book?.path ?? '',
        shared: Boolean(book?.shared),
    };
}

/** The domain a shared book belongs to, taken from its path: /ortak-<domain>/. */
function domainOf(path: string): string {
    const match = /^\/ortak-([^/]+)\//.exec(path);
    return match?.[1] ?? '';
}

const clean = (values: string[]): string[] => values.map((v) => v.trim()).filter(Boolean);

export default function Contacts() {
    return (
        <MailShell section="contacts">
            <ContactsPanel />
        </MailShell>
    );
}

function ContactsPanel() {
    const { client, compose } = useMail();
    const { t, num } = useT();
    const { onePane } = usePanes();
    const show = useToasts((s) => s.show);

    const [books, setBooks] = useState<ContactBook[]>([]);
    const [people, setPeople] = useState<Contact[]>([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    /** The installation has no DAV server. The endpoint says so; the navigation never offers this. */
    const [missing, setMissing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [draft, setDraft] = useState<Draft | null>(null);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    /*
        Where a click wanted to go while the form had unsaved changes.

        Held rather than obeyed or refused: the question is asked in the form itself, the way the
        composer asks it when a half-written message is closed. Discarding somebody's typing to
        follow a click is the thing this exists to stop.
    */
    const [pending, setPending] = useState<Destination | null>(null);
    // What the draft looked like when it was loaded, so "changed" is a fact rather than a flag
    // every field has to remember to set.
    const pristine = useRef('');

    const load = useCallback(async () => {
        if (!client) return;
        setLoading(true);
        try {
            const book = await client.contacts();
            if (!book) {
                setMissing(true);
                return;
            }
            setBooks(book.books);
            setPeople(book.contacts);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        void load();
    }, [load]);

    const writable = useMemo(() => books.filter((b) => !b.shared), [books]);
    const dirty = draft !== null && JSON.stringify(draft) !== pristine.current;

    const open = useCallback((where: Destination, firstBook: ContactBook | undefined) => {
        const next = where === NOTHING ? null : where === 'new' ? blank(firstBook) : draftOf(where);
        setDraft(next);
        pristine.current = next ? JSON.stringify(next) : '';
        setSaved(false);
        setError(null);
    }, []);

    const go = (where: Destination) => {
        if (dirty) {
            setPending(where);
            return;
        }
        open(where, writable[0] ?? books[0]);
    };

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return people;
        return people.filter((p) =>
            [p.name, p.org ?? '', ...p.email, ...p.phone].some((v) => v.toLowerCase().includes(q)),
        );
    }, [people, query]);

    /*
        Liste defterlere bölünüyor: önce kendi kişileri, sonra alan adının ortak defteri.

        Sıra sabit ve bu bir tercih: kendi defteri kişinin her gün baktığı yer, ortak defter ise
        ara sıra. Boş bir grup hiç çizilmiyor — başlığı olan ama altı boş bir bölüm, orada bir şey
        olduğunu söyleyip olmadığını göstermek olurdu.
    */
    const gruplar = useMemo(() => {
        const kendi = shown.filter((p) => !p.shared);
        const ortak = shown.filter((p) => p.shared);
        const ortakDefter = books.find((b) => b.shared);
        const alan = ortakDefter ? /^\/ortak-([^/]+)\//.exec(ortakDefter.path)?.[1] ?? '' : '';
        return [
            { anahtar: 'kendi', baslik: t('contacts.myBook'), kisiler: kendi },
            { anahtar: 'ortak', baslik: t('contacts.sharedBook', { domain: alan }), kisiler: ortak },
        ].filter((g) => g.kisiler.length > 0);
    }, [shown, books, t]);

    // A contact needs something to be. The server refuses an empty one too — this is so the button
    // is honest about it before anybody presses it.
    const savable = Boolean(draft && (draft.name.trim() || clean(draft.email).length));

    const save = async (): Promise<boolean> => {
        if (!client || !draft || !savable) return false;
        setBusy(true);
        try {
            const result = await client.saveContact({
                // Absent rather than empty for a new contact: an empty id is a path, and the server
                // would take it for one.
                ...(draft.id ? { id: draft.id } : {}),
                name: draft.name.trim(),
                email: clean(draft.email),
                phone: clean(draft.phone),
                org: draft.org.trim(),
                note: draft.note.trim(),
                book: draft.book,
            });
            setPeople((list) => {
                const without = list.filter((p) => p.id !== result.id);
                return [...without, result].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            });
            const next = draftOf(result);
            setDraft(next);
            pristine.current = JSON.stringify(next);
            setSaved(true);
            setError(null);
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    /*
        Deleting happens at once and leaves the way back on screen — the shelf every destructive
        action in this application uses, rather than a dialog asking whether somebody meant it.

        What goes back is the card the server handed over as it deleted it, not a contact rebuilt
        from these five fields: the card may carry a photograph this screen never showed.
    */
    const remove = async () => {
        if (!client || !draft?.id) return;
        const name = draft.name || draft.email[0] || '';
        const id = draft.id;
        setBusy(true);
        try {
            const gone = await client.deleteContact(id);
            setPeople((list) => list.filter((p) => p.id !== id));
            open(NOTHING, undefined);
            show({
                message: 'contacts.deleted',
                values: { name },
                undo: gone.card
                    ? () => {
                          void client
                              .restoreContact(id, gone.card)
                              .then(load)
                              .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
                      }
                    : undefined,
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const field = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
    const setLine = (key: 'email' | 'phone', at: number, value: string) =>
        setDraft((d) => (d ? { ...d, [key]: d[key].map((v, i) => (i === at ? value : v)) } : d));
    const dropLine = (key: 'email' | 'phone', at: number) =>
        setDraft((d) => (d ? { ...d, [key]: d[key].filter((_, i) => i !== at) } : d));

    if (missing) {
        return <Empty title={t('contacts.title')} hint={t('contacts.none')} />;
    }

    // Narrow: one thing at a time, the way the mail screen shows a message instead of the list.
    const solo = onePane && draft !== null;

    return (
        <div style={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0 }}>
            {solo ? null : (
                <Pane
                    longList
                    title={t('contacts.title')}
                    count={loading ? t('app.loading') : t('contacts.count', { count: num(shown.length) })}
                    actions={
                        <>
                            <input
                                data-testid="contacts-search"
                                data-hit
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t('contacts.search')}
                                style={input}
                            />
                            <button data-testid="contact-new" data-hit onClick={() => go('new')} style={button(true)}>
                                {t('contacts.new')}
                            </button>
                        </>
                    }
                >
                    {/*
                        Liste artık DEFTER BAŞLIKLARIYLA gruplu, satır başına "ortak" rozetiyle
                        değil.

                        Aynı bilgi, ama bir kez: on kişilik ortak bir defterde eski hâl aynı
                        rozeti on kere çiziyordu ve rozet tekrarlandıkça bir şey söylemez oluyor.
                        Başlık ayrıca defterin ADINI da veriyor — rozet yalnız "ortak" diyordu,
                        hangi firmanın olduğunu değil.
                    */}
                    {gruplar.map((grup) => (
                        <div key={grup.anahtar} style={{ display: 'grid', gap: 2 }}>
                            <div
                                data-testid="contact-book-heading"
                                style={{
                                    fontSize: 'var(--fs-xs)',
                                    fontWeight: 600,
                                    color: 'var(--text-mute)',
                                    padding: '10px 9px 4px',
                                }}
                            >
                                {grup.baslik}
                            </div>
                            {grup.kisiler.map((person) => {
                        const on = person.id === draft?.id;
                        return (
                            <button
                                key={person.id}
                                data-testid="contact-row"
                                data-hit
                                onClick={() => go(person)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    width: '100%',
                                    padding: '7px 9px',
                                    textAlign: 'left',
                                    background: on ? 'var(--accent-bg)' : 'transparent',
                                    border: 'none',
                                    borderRadius: 'var(--r-btn)',
                                }}
                            >
                                <span
                                    style={{
                                        width: 30,
                                        height: 30,
                                        flex: '0 0 auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 'var(--r-pill)',
                                        background: avatarColor(person.email[0] ?? person.name),
                                        color: '#fff',
                                        fontSize: 'var(--fs-2xs)',
                                        fontWeight: 700,
                                    }}
                                >
                                    {initials(person.name || person.email[0] || '?')}
                                </span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span
                                        style={{
                                            display: 'block',
                                            fontSize: 'var(--fs-md)',
                                            fontWeight: on ? 600 : 400,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {person.name}
                                    </span>
                                    {/* Kurum varsa kurum, yoksa adres: bir kişiyi listede ayıran
                                        şey çoğu zaman nerede çalıştığıdır, adresi değil. */}
                                    <span
                                        style={{
                                            display: 'block',
                                            marginTop: 1,
                                            fontSize: 'var(--fs-xs)',
                                            color: 'var(--text-mute)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {person.org || person.email[0] || person.phone[0] || ''}
                                    </span>
                                </span>
                            </button>
                        );
                            })}
                        </div>
                    ))}
                    {!loading && !shown.length ? (
                        <div style={{ padding: '10px 9px', fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                            {people.length ? t('contacts.noMatches') : t('contacts.empty')}
                        </div>
                    ) : null}
                </Pane>
            )}

            {/*
                A failure has to be visible before anything is chosen.

                The error line lower down lives inside the detail pane, and that pane is only drawn
                once somebody has opened a contact. So a load that failed showed an EMPTY ADDRESS
                BOOK — not a broken one — and there was no way to reach the sentence explaining it.
                The same silence covered the undo path.
            */}
            {draft === null && error ? (
                <Detail>
                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }} data-testid="contacts-error">
                        {error}
                    </div>
                </Detail>
            ) : null}

            {draft === null ? (
                error ? null : onePane ? null : <Empty title={t('contacts.title')} hint={t('contacts.pick')} />
            ) : (
                <Detail>
                    {solo ? (
                        <button
                            data-testid="contact-back"
                            data-hit
                            onClick={() => go(NOTHING)}
                            style={{ ...button(), justifySelf: 'start', display: 'inline-flex', alignItems: 'center' }}
                        >
                            ← {t('contacts.title')}
                        </button>
                    ) : null}

                    {/*
                        The unsaved question, in the form rather than over it.

                        Three answers, the same three the composer offers when a half-written
                        message is closed: keep it, drop it, or go back to it. A dialog would
                        interrupt the ninety-nine intentional clicks to catch the one mistake.
                    */}
                    {pending !== null ? (
                        <div
                            data-testid="contact-unsaved"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                                padding: '9px 11px',
                                background: 'var(--amber-bg)',
                                border: '1px solid var(--amber)',
                                borderRadius: 'var(--r-btn)',
                            }}
                        >
                            <span style={{ flex: 1, minWidth: 180, fontSize: 'var(--fs-sm)', color: 'var(--amber)' }}>
                                {t('contacts.unsavedQuestion')}
                            </span>
                            <button
                                disabled={busy || !savable}
                                onClick={() => {
                                    void save().then((ok) => {
                                        if (!ok) return;
                                        open(pending, writable[0] ?? books[0]);
                                        setPending(null);
                                    });
                                }}
                                style={button(true)}
                            >
                                {t('app.save')}
                            </button>
                            <button
                                onClick={() => {
                                    open(pending, writable[0] ?? books[0]);
                                    setPending(null);
                                }}
                                style={button()}
                            >
                                {t('compose.dontSave')}
                            </button>
                            <button onClick={() => setPending(null)} style={button()}>
                                {t('contacts.keepEditing')}
                            </button>
                        </div>
                    ) : null}

                    {/*
                        A shared entry says so, and says whose it is.

                        The rights on the server allow it: everyone with an address in the domain
                        may write there. That is exactly why the notice is here — a change made in
                        this book is a change to everybody's copy, and nothing else on the screen
                        would say so.
                    */}
                    {draft.shared ? (
                        <div
                            data-testid="contact-shared-notice"
                            style={{
                                padding: '9px 11px',
                                background: 'var(--chip-bg)',
                                border: '1px solid var(--border2)',
                                borderRadius: 'var(--r-btn)',
                                fontSize: 'var(--fs-sm)',
                                color: 'var(--text-dim)',
                                lineHeight: 1.5,
                            }}
                        >
                            {t('contacts.sharedNotice', { domain: domainOf(draft.book) })}
                        </div>
                    ) : null}

                    <div style={card}>
                        <label style={{ display: 'grid', gap: 5 }}>
                            <span className="kicker">// {t('contacts.name')}</span>
                            <input
                                data-testid="contact-name"
                                data-hit
                                value={draft.name}
                                onChange={(e) => field({ name: e.target.value })}
                                style={{ ...input, flex: 'none', width: '100%' }}
                            />
                        </label>

                        <Lines
                            testid="contact-email"
                            label={t('contacts.emails')}
                            values={draft.email}
                            addLabel={t('contacts.addEmail')}
                            type="email"
                            onChange={(at, value) => setLine('email', at, value)}
                            onRemove={(at) => dropLine('email', at)}
                            onAdd={() => field({ email: [...draft.email, ''] })}
                            action={(value) =>
                                value.trim() ? (
                                    <button
                                        data-hit
                                        title={t('contacts.writeTo', { address: value.trim() })}
                                        onClick={() => compose({ mode: 'new', source: null, to: value.trim() })}
                                        style={button()}
                                    >
                                        {t('contacts.write')}
                                    </button>
                                ) : null
                            }
                        />

                        <Lines
                            testid="contact-phone"
                            label={t('contacts.phones')}
                            values={draft.phone}
                            addLabel={t('contacts.addPhone')}
                            type="tel"
                            onChange={(at, value) => setLine('phone', at, value)}
                            onRemove={(at) => dropLine('phone', at)}
                            onAdd={() => field({ phone: [...draft.phone, ''] })}
                        />

                        <label style={{ display: 'grid', gap: 5 }}>
                            <span className="kicker">// {t('contacts.organization')}</span>
                            <input
                                data-testid="contact-org"
                                data-hit
                                value={draft.org}
                                onChange={(e) => field({ org: e.target.value })}
                                style={{ ...input, flex: 'none', width: '100%' }}
                            />
                        </label>

                        <label style={{ display: 'grid', gap: 5 }}>
                            <span className="kicker">// {t('contacts.note')}</span>
                            <textarea
                                data-testid="contact-note"
                                data-hit
                                value={draft.note}
                                onChange={(e) => field({ note: e.target.value })}
                                rows={3}
                                style={{
                                    ...input,
                                    flex: 'none',
                                    width: '100%',
                                    height: 'auto',
                                    padding: '8px 10px',
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                    lineHeight: 1.5,
                                }}
                            />
                        </label>

                        {/*
                            Which book a new contact goes into — and only when there is a choice to
                            make. One book is not a choice, and a control that decides nothing is
                            one somebody reaches for twice.
                        */}
                        {!draft.id && books.length > 1 ? (
                            <label style={{ display: 'grid', gap: 5 }}>
                                <span className="kicker">// {t('contacts.book')}</span>
                                <select
                                    data-testid="contact-book"
                                data-hit
                                    value={draft.book}
                                    onChange={(e) => {
                                        const picked = books.find((b) => b.path === e.target.value);
                                        field({ book: e.target.value, shared: Boolean(picked?.shared) });
                                    }}
                                    style={{ ...input, flex: 'none', width: '100%' }}
                                >
                                    {books.map((b) => (
                                        <option key={b.path} value={b.path}>
                                            {b.shared ? `${b.name} (${t('contacts.shared')})` : b.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : null}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <button
                                data-testid="contact-save"
                                data-hit
                                disabled={busy || !savable || !dirty}
                                onClick={() => void save()}
                                style={{ ...button(true), opacity: !savable || !dirty ? 0.5 : 1 }}
                            >
                                {t('app.save')}
                            </button>
                            {draft.id ? (
                                <button
                                    data-testid="contact-delete"
                                    data-hit
                                    disabled={busy}
                                    onClick={() => void remove()}
                                    style={{ ...button(), color: 'var(--red)', borderColor: 'var(--red)' }}
                                >
                                    {t('contacts.delete')}
                                </button>
                            ) : null}
                            {saved && !dirty ? <span className="kicker">// {t('contacts.saved')}</span> : null}
                            {!savable ? (
                                <span className="kicker" style={{ color: 'var(--text-mute)' }}>
                                    // {t('contacts.needSomething')}
                                </span>
                            ) : null}
                        </div>

                        {error ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{error}</div> : null}

                        {/*
                            CardDAV'ın varlık sebebi bu ve kullanıcıya söylenmesi gerekiyor: buraya
                            yazılan kişi telefonda da beliriyor. Söylenmediğinde rehber, webmail'in
                            içinde kalan ikinci bir defter gibi görünüyor ve kimse ona kişi yazmıyor.
                        */}
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', lineHeight: 1.5 }}>
                            {t('contacts.syncNote')}
                        </div>
                    </div>
                </Detail>
            )}
        </div>
    );
}

/** A field somebody can have several of: every address, every number, one line each. */
function Lines({
    testid,
    label,
    values,
    addLabel,
    type,
    onChange,
    onRemove,
    onAdd,
    action,
}: {
    testid: string;
    label: string;
    values: string[];
    addLabel: string;
    type: 'email' | 'tel';
    onChange: (at: number, value: string) => void;
    onRemove: (at: number) => void;
    onAdd: () => void;
    action?: (value: string) => React.ReactNode;
}) {
    const { t } = useT();
    return (
        <div style={{ display: 'grid', gap: 5 }}>
            <span className="kicker">// {label}</span>
            {values.map((value, at) => (
                <div key={at} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                        data-testid={at === 0 ? testid : `${testid}-${at}`}
                        data-hit
                        /*
                            Named one by one. The `// e-posta` line above says what the group is
                            but belongs to no field in particular, so a screen reader arriving at
                            the second address announced an unnamed text box — axe called it, on
                            this screen, before anybody shipped it.
                        */
                        aria-label={`${label} ${at + 1}`}
                        value={value}
                        type={type}
                        onChange={(e) => onChange(at, e.target.value)}
                        style={input}
                    />
                    {action?.(value)}
                    {/* The last line stays: a form with no line at all offers nowhere to type. */}
                    {values.length > 1 ? (
                        <button
                            data-hit
                            title={t('contacts.removeLine')}
                            aria-label={t('contacts.removeLine')}
                            onClick={() => onRemove(at)}
                            style={{ ...button(), width: 30, padding: 0 }}
                        >
                            ×
                        </button>
                    ) : null}
                </div>
            ))}
            <button data-hit onClick={onAdd} style={{ ...button(), justifySelf: 'start' }}>
                {addLabel}
            </button>
        </div>
    );
}
